import { Column } from '@devexpress/dx-react-grid';
import { TFunction } from 'i18next';
import { isMimeType } from 'validator';

export const MIME_TYPES = {
  IMAGES: [
    'image/apng',
    'image/avif',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/webp',
  ],
  DOCUMENTS: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  SPREADSHEETS: [
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  VIDEOS: ['video/mp4', 'video/webm', 'video/ogg'],
  AUDIO: ['audio/mpeg', 'audio/wav', 'audio/ogg'],
} as const;

/**
 * Converts 'yes'/'no' string keys to boolean
 * @param yesNo
 */
export const yesNoToBool = (yesNo: string) => {
  return yesNo === 'yes';
};

/**
 * Converts boolean to 'yes'/'no' string keys
 * @param bool
 */
export const boolToYesNo = (bool: boolean) => {
  return bool ? 'yes' : 'no';
};

/**
 * Returns true when a string is empty or whitespace, otherwise returns false
 * @param str
 */
export const isEmptyOrSpaces = (str: string | null | undefined) => {
  return (
    str === undefined || str === null || String(str).match(/^\s*$/) !== null
  );
};

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

/**
 * Returns and Empty Guid select option with specified label
 * @param label
 */
export const emptyGuidOption = (label: string) => ({
  label,
  value: EMPTY_GUID,
});

/**
 * Returns messages for paging component of React-Grid
 * @param t
 */
export const getPagingMessages = (t: TFunction) => ({
  showAll: t('all'),
  rowsPerPage: t('rowsPerPage'),
  info: t('pageInfo'),
});

/**
 * Returns messages for table component of React-Grid
 * @param t
 */
export const getTableMessages = (t: TFunction) => ({
  noData: t('noData'),
});

/**
 * Trims text and add ellipses when threshold number of characters is reached.
 * @param text
 * @param threshold
 */
export const trimText = (text: string, threshold: number) => {
  if (text.length <= threshold) return text;
  return text.substr(0, threshold).concat('...');
};

/**
 * Returns true when a string contains only a non-negative number
 * @param str
 */
export const isNonNegativeNumber = (str: string | null | undefined) => {
  return (
    str !== undefined &&
    str !== null &&
    /^([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/.test(str)
  );
};

/**
 * Returns true when a string contains only a positive integer
 * @param str
 * @constructor
 */
export const isPositiveInteger = (str: string | null | undefined) => {
  return str !== undefined && str !== null && /^\+?[1-9]\d*$/.test(str);
};

/**
 * Splits text into words while combining word combinations not greater than minWordSize
 * @param text
 * @param minWordSize
 */
export const splitText = (text: string, minWordSize: number): string[] => {
  const words = text.split(/\s/g);
  const combinedWords: string[] = [];
  for (let i = 0; i < words.length; i++) {
    const currentWord = words[i];
    if (combinedWords.length === 0) {
      combinedWords.push(currentWord);
      continue;
    }
    const previousWord = combinedWords[combinedWords.length - 1];
    const comboWord = `${previousWord} ${currentWord}`;

    if (comboWord.length <= minWordSize) {
      combinedWords[combinedWords.length - 1] = comboWord;
    } else {
      combinedWords.push(currentWord);
    }
  }
  return combinedWords;
};

/**
 * Generates CSV content from rows and headers
 * @param rows
 * @param allColumns
 */
export const generateCsvContent = (
  rows: any[],
  allColumns: Column[],
): string => {
  const csvRows = [];
  const headers = allColumns.map((column) => column.title);
  csvRows.push(headers.join(','));
  for (const row of rows) {
    const values = allColumns.map((column) =>
      stringifyCsvCellValue(row[column.name]),
    );
    csvRows.push(values.join(','));
  }
  return csvRows.join('\n');
};

/**
 * Converts a cell value to a CSV-safe quoted string (RFC-style quoting).
 * Shared by DevExpress `generateCsvContent` and MRT `generateCsvContentFromMrtColumnDefs`.
 */
export const stringifyCsvCellValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  const stringValue = String(value);
  return `"${stringValue.replace(/"/g, '""')}"`;
};

/**
 * Extracts numeric and non-numeric value from a string
 * @param value
 */
export const extractNumericValue = (
  value: string,
): { numeric: number; text: string } => {
  const numericMatches = value.match(/\d+/g);
  const numeric = numericMatches ? parseInt(numericMatches.join(''), 10) : 0;

  const textMatches = value.match(/[a-zA-Z]+/g);
  const text = textMatches ? textMatches.join('') : '';

  return { numeric, text };
};

/**
 * Enhanced select 'None' option text
 * @param t
 */
export const selectNoneOption = (t: TFunction): string =>
  `-- ${t('selectNone')} --`;

/**
 * Extracts hostname from a url
 * @param url
 */
export const getUrlHostname = (url: string) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    return url;
  }
};

/**
 * Determines if a given path is a file path.
 *
 * This function checks if the provided path is a valid file path by analyzing
 * its format. It supports Windows drive letters, UNC paths, Unix absolute paths,
 * and relative paths. It trims the input and removes surrounding quotes before
 * performing the checks.
 *
 * @param {string} path - The path to validate.
 * @returns {boolean} `true` if the path is a file path, otherwise `false`.
 */
export const isFilePath = (path: string): boolean => {
  if (!path) {
    return false;
  }

  const cleanPath = path.trim().replace(/^["']|["']$/g, '');

  // Windows drive letter (C:\, D:\, etc.)
  if (/^[a-zA-Z]:\\/.test(cleanPath)) {
    return true;
  }

  // UNC path (\\server\share)
  if (cleanPath.startsWith('\\\\')) {
    return true;
  }

  // Unix absolute paths (but not URLs starting with //)
  if (cleanPath.startsWith('/') && !cleanPath.startsWith('//')) {
    return true;
  }

  // Relative paths
  return /^\.\.?[\\/]/.test(cleanPath);
};

/**
 * Validates a given URL and returns an error message if the URL is invalid.
 *
 * This function checks if the provided URL is empty and returns an appropriate
 * error message using the provided translation function `t`. If the URL is valid,
 * it returns `null`.
 *
 * @param {string} url - The URL to validate.
 * @param {TFunction} t - The translation function used to generate error messages.
 * @returns {string | null} An error message if the URL is invalid, or `null` if the URL is valid.
 */
export const validateLinkUrl = (url: string, t: TFunction): string | null => {
  if (!url || url.trim() === '') {
    // Return error message if URL is empty
    return t('urlIsRequired');
  }

  return null; // Valid URL
};

/**
 * Checks if a given file type is accepted based on a list of accepted MIME types.
 *
 * This function validates the provided file type against a list of accepted types.
 * It ensures the file type is a valid MIME type and checks for exact matches or
 * wildcard matches (e.g., "image/*"). If no accepted types are provided, all valid
 * MIME types are considered accepted.
 *
 * @param {string} fileType - The MIME type of the file to check.
 * @param {string[]} acceptedTypes - An array of accepted MIME types or wildcard patterns.
 * @returns {boolean} `true` if the file type is accepted, otherwise `false`.
 */
export function isFileTypeAccepted(
  fileType: string,
  acceptedTypes: string[],
): boolean {
  if (!fileType || !isMimeType(fileType)) {
    return false;
  }

  if (!acceptedTypes || acceptedTypes.length === 0) {
    return true; // If no specific types are set, acceptedTypes all valid MIME types
  }
  return acceptedTypes.some((acceptedType) => {
    const trimmedType = acceptedType.trim();

    // Validate the accepted type format
    if (
      !trimmedType ||
      (!isMimeType(trimmedType) && !trimmedType.endsWith('/*'))
    ) {
      return false;
    }

    if (fileType === trimmedType) {
      return true; // Exact match
    }

    if (trimmedType.endsWith('/*')) {
      // Check for wildcard match (e.g., "image/*")
      const baseType = trimmedType.slice(0, -2); // Remove '/*'
      return fileType.startsWith(baseType + '/');
    }

    return false;
  });
}

/**
 * Parses a file type acceptedTypes string into an array of MIME types.
 *
 * This function takes a comma-separated string of accepted file types
 * (e.g., "image/png, image/jpeg") and returns an array of trimmed MIME types.
 * Empty or invalid entries are filtered out.
 *
 * @param {string} accept - The acceptedTypes string to parse.
 * @returns {string[]} An array of valid MIME types.
 */
export function parseFileTypeAcceptString(accept: string): string[] {
  return accept
    .split(',')
    .map((type) => type.trim())
    .filter(Boolean);
}
